import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import ICON_JPN from '@/assets/flag/icon_JPN.png';
import ICON_KOR from '@/assets/flag/icon_KOR.png';
import { setExchangeRate } from '@/util/exchangeRate';

type ExchangeRateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rate: number) => void;
  currentRate: number;
};

const MIN_RATE = 800;
const MAX_RATE = 1200;

function ExchangeRateModal({
  isOpen,
  onClose,
  onSubmit,
  currentRate,
}: ExchangeRateModalProps) {
  const [rate, setRate] = useState(currentRate);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRate(currentRate);
      setError('');
    }
  }, [isOpen, currentRate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setRate(value ? Number(value) : 0);
    setError('');
  };

  const handleSubmit = () => {
    if (!rate || rate < MIN_RATE || rate > MAX_RATE) {
      setError('정확한 금액을 입력해주세요');
      return;
    }
    // 로컬 스토리지에 환율 저장
    setExchangeRate(rate);
    onSubmit(rate);
    onClose();
  };

  if (!isOpen) return null;

  const hasError =
    !!error || (rate !== 0 && (rate < MIN_RATE || rate > MAX_RATE));

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Title>외화 수납 환율 수정하기</Title>
          <Subtitle>적용하실 환율을 입력해주세요</Subtitle>
        </ModalHeader>

        <ExchangeRow>
          <CurrencySection>
            <Flag src={ICON_JPN} />
            <InputBoxWrapper>
              <CurrencyLabel>일본 JPY</CurrencyLabel>
              <InputBox>
                <FixedValue>100</FixedValue>
                <Unit>엔</Unit>
              </InputBox>
            </InputBoxWrapper>
          </CurrencySection>

          <EqualSign>=</EqualSign>

          <CurrencySection>
            <Flag src={ICON_KOR} />
            <InputBoxWrapper>
              <CurrencyLabel>원화 KRW</CurrencyLabel>
              <InputWrapper hasError={hasError}>
                <AmountInput
                  type='text'
                  value={rate || ''}
                  onChange={handleChange}
                  placeholder='0'
                  onFocus={() => {
                    setRate(0);
                  }}
                />
                <Unit>원</Unit>
              </InputWrapper>
            </InputBoxWrapper>
          </CurrencySection>
        </ExchangeRow>

        <MessageArea>
          {hasError && <ErrorText>정확한 금액을 입력해주세요</ErrorText>}
          <HintText>
            {MIN_RATE}원~{MAX_RATE.toLocaleString()}원 사이의 금액만
            입력가능합니다.
          </HintText>
        </MessageArea>

        <SubmitButton onClick={handleSubmit} disabled={hasError}>
          수정하기
        </SubmitButton>
      </ModalContent>
    </ModalOverlay>
  );
}

export default ExchangeRateModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.12);
  padding: 40px;
`;

const ModalHeader = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h2`
  color: var(--Mono-100, #030303);

  /* WebHead/21_BD */
  font-family: Pretendard;
  font-size: 21px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 31.5px */
  letter-spacing: -0.42px;
`;

const Subtitle = styled.p`
  color: var(--Mono-65, #5f6165);

  /* WebBody/16_MD */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
`;

const ExchangeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
`;

const CurrencySection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CurrencyLabel = styled.span`
  color: var(--Mono-65, #5f6165);

  /* WebBody/12_MD */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: -0.24px;
`;

const InputBox = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid var(--Mono-10, #e5e6e8);
  background: var(--Mono-10, #e5e6e8);
`;

const FixedValue = styled.span`
  text-align: right;
  flex: 1;
  color: var(--Mono-50, #80848a);

  /* WebBody/16_MD */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
`;

const InputWrapper = styled.div<{ hasError: boolean }>`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid
    ${({ hasError }) => (hasError ? '#FF2D55' : 'var(--Mono-80, #3a3b3e)')};
  background: var(--Mono-00, #fff);
`;

const AmountInput = styled.input`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 60px;

  /* WebBody/16_MD */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
  text-align: right;
  background: transparent;
  border: none;
  outline: none;
  color: var(--Mono-50, #80848a);

  &::placeholder {
    color: #ccc;
  }
`;

const Unit = styled.span`
  color: var(--Mono-65, #5f6165);

  /* WebBody/12_MD */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: -0.24px;
`;

const EqualSign = styled.span`
  color: #1a1a1a;
  font-family: Pretendard;
  font-size: 32px;
  font-weight: 600;
`;

const MessageArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 32px;
`;

const ErrorText = styled.span`
  margin-top: -8px;
  color: var(--Red-80, #ff2d55);
  text-align: right;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: -0.32px;
`;

const HintText = styled.span`
  color: var(--Mono-65, #5f6165);

  /* WebBody/14_MD */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 56px;
  border-radius: 8px;
  border: none;
  background: #246cf6;
  color: #fff;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background: #a7c4fb;
    cursor: not-allowed;
  }
`;

const Flag = styled.img`
  width: 48px;
  height: 48px;
`;

const InputBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 216px;
  height: 64px;
  padding: 6px 6px 6px 12px;
  border-radius: var(--Radius-4-Input, 4px);
  background: var(--Mono-05, #f5f6f7);
`;
